/* @jsx h */
import { defineComponent, h, computed, Ref } from "vue";
import { Order, SuccesfullPartialPayment } from "@lemmonhq/types";

import { PaymentGroup } from "./PaymentGroup";
import { PaymentItem } from "./PaymentItem";
import { getOrderItemsGrouped } from "./paymentUtils";

export type PartialPaymentContentProps = {
  orders: Order[];
  partialPayments: SuccesfullPartialPayment[];
  selectedItems: Record<number, number>;
  /** Omitted for a read-only list: no checkboxes, rows not selectable. */
  onSelectItem?: (id: number, qty: number) => void;
  expandedGroups: Ref<Set<string>>;
};

export const PartialPaymentContent =
  defineComponent<PartialPaymentContentProps>({
    name: "PartialPaymentContent",
    props: [
      "orders",
      "partialPayments",
      "selectedItems",
      "onSelectItem",
      "expandedGroups",
    ],
    setup(props) {
      const { expandedGroups } = props;
      const toggleGroup = (groupId: string) => {
        const newSet = new Set(expandedGroups.value);
        if (newSet.has(groupId)) {
          newSet.delete(groupId);
        } else {
          newSet.add(groupId);
        }
        expandedGroups.value = newSet;
      };

      const groupedItems = computed(() => {
        return getOrderItemsGrouped(props.orders);
      });

      return () => (
        <div class="p-1 lg:p-4 flex flex-col overflow-y-hidden select-none">
          <div class="space-y-2 flex flex-col flex-1 overflow-y-auto text-sm lg:text-1xl text-left">
            {groupedItems.value.map(
              ({ groupId, items, totalQuantity, firstItem }) => {
                if (items.length == 1) {
                  const item = items[0];
                  return (
                    <PaymentItem
                      key={item.id}
                      item={item}
                      selectedQuantity={props.selectedItems[item.id] || 0}
                      onSelect={props.onSelectItem}
                      partialPayments={props.partialPayments}
                    />
                  );
                } else {
                  return (
                    <PaymentGroup
                      key={groupId}
                      items={items}
                      firstItem={firstItem}
                      totalQuantity={totalQuantity}
                      isExpanded={expandedGroups.value.has(groupId)}
                      selectedItems={props.selectedItems}
                      partialPayments={props.partialPayments}
                      onSelect={props.onSelectItem}
                      onToggle={() => toggleGroup(groupId)}
                    />
                  );
                }
              },
            )}
          </div>
        </div>
      );
    },
  });
